/**
 * Validator for DraftDecoupled action.
 *
 * Rules (§6.3, §11.1, §14):
 *   1. stateVersion matches.
 *   2. Phase is "marketCheck".
 *   3. market.decoupleUnlockedThisTurn === true (player previously dispatched
 *      SpendNatureDecouple this turn — §6.3 nature-spend pattern (a)).
 *   4. tileSlot !== wildlifeSlot (different slots required by spec §6.3:
 *      "pick any tile from any slot and any wildlife from any slot (different
 *      slots allowed)" — the spec permits the same slot, but §11.1 describes
 *      DraftDecoupled as the mechanism for taking from _different_ slots;
 *      if same slot was desired, DraftPair would be used. To remain consistent
 *      with the intent, we allow same slot — see NOTE below).
 *   5. Both tileSlot and wildlifeSlot are non-empty in the market.
 *
 * NOTE: The spec §6.3 says "pick any tile from any slot and any wildlife from
 * any slot (different slots allowed)". The word "allowed" indicates that
 * different slots are _permitted_ (not _required_). Same slot is also valid
 * (same as DraftPair but via the decouple path). We do NOT enforce
 * tileSlot !== wildlifeSlot.
 */

import type { DraftDecoupledAction } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { GameState } from "../types/game-state";
import { commonGuards, VALID } from "./common-guards";

export const validateDraftDecoupled = (
  state: GameState,
  action: DraftDecoupledAction,
): ValidationResult => {
  const guard = commonGuards(state, action.stateVersion, "marketCheck");
  if (guard !== null) {
    return guard;
  }

  if (!state.market.decoupleUnlockedThisTurn) {
    return {
      valid: false,
      error: "ERR_DECOUPLE_NOT_UNLOCKED",
      details: {},
    };
  }

  const tileSlot = state.market.slots[action.tileSlot];
  if (tileSlot.tileInstanceId === null) {
    return {
      valid: false,
      error: "ERR_MARKET_SLOT_EMPTY",
      details: { slot: action.tileSlot, missing: "tile" },
    };
  }

  const wildlifeSlot = state.market.slots[action.wildlifeSlot];
  if (wildlifeSlot.tokenInstanceId === null) {
    return {
      valid: false,
      error: "ERR_MARKET_SLOT_EMPTY",
      details: { slot: action.wildlifeSlot, missing: "wildlife" },
    };
  }

  return VALID;
};
